import React, { useEffect, useState } from 'react'
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    FormControlLabel, InputAdornment, Radio, RadioGroup, Switch, TextField, Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { filterButtonSx } from '../../common/sharedStyles'
import { MODE_META, policyMode } from './archivalUtils'

const NUMBER_FIELDS = [
    { key: 'hotMonths', label: 'Keep in collection' },
    { key: 'warmMonths', label: 'Then in archive' },
    { key: 'maxMonthsPerRun', label: 'Catch-up limit' },
    { key: 'chunkSize', label: 'Batch size' },
    { key: 'sleepMs', label: 'Pause between batches (ms)' },
]
const FLAG_FIELDS = ['enabled', 'dryRun', 's3Enabled']

const toForm = (p) => {
    const f = {}
    NUMBER_FIELDS.forEach(({ key }) => { f[key] = p?.[key] == null ? '' : String(p[key]) })
    FLAG_FIELDS.forEach((key) => { f[key] = Boolean(p?.[key]) })
    return f
}
const whole = (v) => (/^\d+$/.test(String(v).trim()) ? Number(String(v).trim()) : null)

function Toggle({ checked, onChange, label, help }) {
    return (
        <FormControlLabel
            sx={{ alignItems: 'flex-start', m: 0, gap: 1 }}
            control={<Switch size='small' checked={checked} onChange={(e) => onChange(e.target.checked)} sx={{ mt: 0.25 }} />}
            label={
                <Box>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>{label}</Typography>
                    {help && <Typography variant='caption' sx={{ color: 'text.secondary' }}>{help}</Typography>}
                </Box>
            }
        />
    )
}

/**
 * Edit one collection's retention. The full policy object is sent back (PUT) with only the edited fields changed;
 * the backend enforces the per-collection minimums and answers 400 with the reason, shown once, inline.
 */
export default function PolicyDialog({ open, item, floors, globalS3Enabled = true, busy, error, onCancel, onSave }) {
    const policy = item?.policy
    const [form, setForm] = useState(() => toForm(policy))
    const [localError, setLocalError] = useState('')
    const [advanced, setAdvanced] = useState(false)

    useEffect(() => {
        if (open) { setForm(toForm(policy)); setLocalError(''); setAdvanced(false) }
    }, [open, policy])

    const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }))
    // One mode on screen, two flags on the wire. Paused leaves dryRun as it was, so Resume returns to the last mode.
    const mode = policyMode(form)
    const setMode = (m) => setForm((f) => (m === 'PAUSED' ? { ...f, enabled: false } : { ...f, enabled: true, dryRun: m === 'DRY_RUN' }))
    const minHot = floors?.minHotMonths ?? policy?.minHotMonths
    const minTotal = floors?.minHotPlusWarmMonths ?? policy?.minHotPlusWarmMonths
    const hot = whole(form.hotMonths)
    const warm = whole(form.warmMonths)

    const submit = () => {
        const out = { ...policy }
        for (const { key, label } of NUMBER_FIELDS) {
            const v = whole(form[key])
            if (v == null) {
                setLocalError(`${label} must be a whole number.`)
                if (!['hotMonths', 'warmMonths'].includes(key)) setAdvanced(true)
                return
            }
            out[key] = v
        }
        FLAG_FIELDS.forEach((key) => { out[key] = form[key] })
        setLocalError('')
        onSave(out)
    }

    const shownError = localError || error
    const monthsInput = { endAdornment: <InputAdornment position='end'>months</InputAdornment> }

    return (
        <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth='sm' fullWidth>
            <DialogTitle sx={{ fontWeight: 700, pb: 0 }}>
                Edit retention
                <Typography component='div' sx={{ fontFamily: 'monospace', fontSize: '0.8125rem', color: 'text.secondary', mt: 0.25 }}>{item?.collectionName}</Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                {shownError && <Alert severity='error' sx={{ mb: 2 }}>{shownError}</Alert>}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <TextField
                        size='small' type='number' label='Keep in collection' value={form.hotMonths}
                        onChange={(e) => setField('hotMonths', e.target.value)}
                        inputProps={{ min: 0, step: 1 }} InputProps={monthsInput}
                        helperText={minHot != null ? `Minimum ${minHot}` : ' '}
                    />
                    <TextField
                        size='small' type='number' label='Then in archive' value={form.warmMonths}
                        onChange={(e) => setField('warmMonths', e.target.value)}
                        inputProps={{ min: 0, step: 1 }} InputProps={monthsInput}
                        helperText={minTotal != null ? `Collection + archive at least ${minTotal}` : ' '}
                    />
                </Box>
                <Typography variant='body2' sx={{ mt: 1, mb: 2, minHeight: 20 }}>
                    {hot != null && warm != null && (
                        !form.s3Enabled
                            ? <>Data older than <b>{hot + warm} months</b> stays in the archive collection.</>
                            : globalS3Enabled
                                ? <>Data older than <b>{hot + warm} months</b> moves to S3.</>
                                : <>Data older than <b>{hot + warm} months</b> moves to S3 once the S3 tier is switched on; until then it stays in the archive collection.</>
                    )}
                </Typography>

                <Typography variant='body2' sx={{ fontWeight: 600, mb: 0.5 }}>Mode</Typography>
                <RadioGroup
                    value={mode}
                    onChange={(e) => setMode(e.target.value)}
                    sx={{ gap: 0.75, mb: 1.5 }}
                >
                    {['PAUSED', 'DRY_RUN', 'LIVE'].map((m) => (
                        <FormControlLabel
                            key={m}
                            value={m}
                            sx={{ alignItems: 'flex-start', m: 0, gap: 1 }}
                            control={<Radio size='small' sx={{ p: 0.25 }} />}
                            label={
                                <Box>
                                    <Typography variant='body2' sx={{ fontWeight: 600 }}>{MODE_META[m].label}</Typography>
                                    <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                                        {m === 'PAUSED' && policy?.disabledReason ? `${MODE_META[m].help} ${policy.disabledReason}.` : MODE_META[m].help}
                                        {m === 'PAUSED' && mode === 'PAUSED' && ` Resume returns it to ${form.dryRun ? 'dry run' : 'live'}.`}
                                    </Typography>
                                </Box>
                            }
                        />
                    ))}
                </RadioGroup>
                {mode === 'LIVE' && policyMode(policy) !== 'LIVE' && (
                    <Alert severity='warning' sx={{ mb: 1.5 }}>
                        Live runs move data out of the collection. Check the dry-run counts in Run history first.
                    </Alert>
                )}
                <Toggle checked={form.s3Enabled} onChange={(v) => setField('s3Enabled', v)} label='Send to S3'
                    help='Off keeps old data in the archive collection.' />

                <Accordion
                    expanded={advanced}
                    onChange={(_, v) => setAdvanced(v)}
                    disableGutters
                    elevation={0}
                    sx={{ mt: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, '&:before': { display: 'none' } }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 1 } }}>
                        <Typography variant='body2' sx={{ fontWeight: 600 }}>Advanced</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
                        <TextField
                            size='small' type='number' label='Catch-up limit' value={form.maxMonthsPerRun}
                            onChange={(e) => setField('maxMonthsPerRun', e.target.value)}
                            inputProps={{ min: 0, step: 1 }} InputProps={monthsInput}
                            helperText='Months moved per run while catching up on old data. Once caught up, each monthly run moves one month.'
                        />
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                            <TextField
                                size='small' type='number' label='Batch size' value={form.chunkSize}
                                onChange={(e) => setField('chunkSize', e.target.value)}
                                inputProps={{ min: 0, step: 1 }} helperText='Documents per batch'
                            />
                            <TextField
                                size='small' type='number' label='Pause between batches (ms)' value={form.sleepMs}
                                onChange={(e) => setField('sleepMs', e.target.value)}
                                inputProps={{ min: 0, step: 1 }} helperText='Eases load on the live database'
                            />
                        </Box>
                    </AccordionDetails>
                </Accordion>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button size='small' sx={filterButtonSx('reset')} onClick={onCancel} disabled={busy}>Cancel</Button>
                <Button size='small' variant='contained' onClick={submit} disabled={busy || !policy} sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {busy ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
